import React from 'react';
import { Mui } from '@leap4/frontend';
import { IRowIssue } from './appAdsLines.api';

const { Alert, AlertTitle, Box, Typography } = Mui;

/**
 * Lists the rows that stopped an import, with the line number from the user's own file.
 *
 * Imports are all-or-nothing, so this is the whole story when one is refused: nothing was
 * saved, and here is precisely what to fix. Row numbers are the ones visible in the source
 * file or paste (header offset already applied), because "row 14" is only useful if it points
 * at the same line the user is looking at.
 */
export const RowIssueList: React.FC<{
    issues: IRowIssue[];
    rowsRead?: number;
    /** Shown above the list. Defaults to the standard refusal wording. */
    title?: string;
    max?: number;
}> = ({ issues, rowsRead, title, max = 12 }) => {
    if (!issues?.length) return null;

    const shown = issues.slice(0, max);
    const hidden = issues.length - shown.length;

    return (
        <Alert severity="error" sx={{ mt: 2 }}>
            <AlertTitle>
                {title ||
                    `${issues.length}${rowsRead ? ` of ${rowsRead}` : ''} row(s) couldn't be read — nothing was imported`}
            </AlertTitle>

            <Typography variant="body2" gutterBottom>
                Fix these and try again. Nothing has been saved.
            </Typography>

            <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                {shown.map((issue, i) => (
                    <li key={`${issue.row}-${i}`}>
                        <Typography variant="body2" component="span">
                            <strong>Row {issue.row}:</strong> {issue.reason}
                        </Typography>
                        {issue.text && (
                            <Typography
                                variant="caption"
                                component="div"
                                sx={{ fontFamily: 'monospace', opacity: 0.8, wordBreak: 'break-all' }}
                            >
                                {issue.text}
                            </Typography>
                        )}
                    </li>
                ))}
            </Box>

            {hidden > 0 && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                    …and {hidden} more.
                </Typography>
            )}
        </Alert>
    );
};

export default RowIssueList;
